import {
  P2GBox,
  P2GButtonGroup,
  P2GButtonGroupItem,
  SvgEdit,
  P2GLink,
  P2GText,
  P2GAlert,
  useP2GMediaQueryOnly,
  P2GDropdown
} from '@parcel2go/core-ui';
import { useContext, useEffect, useState } from 'react';
import {
  ConsignmentApiModel,
  Dimensions,
  MarketplaceOrderApiModel,
  QuoteExtraApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { SaveItemsAction } from '../../../lib/context/SmartSendContextModels';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import {
  CollectionDateHasChanged,
  ConsignmentHasChanged,
  CustomerHasChanged,
  DimensionsHaveChanged,
  ExtrasHaveChanged,
  MetaHasChanged,
  OrderLinesHaveChanged
} from '../../../lib/helpers/SmartSendSocketHelper';
import { GetItemWeight, IsReturnCollection } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { RequiresCustoms, RequiresTaxIdsButNotCustoms } from '../../../lib/helpers/SmartSendValidation';
import { ToCommaList } from '../../../lib/helpers/StringHelper';
import { CustomerSelection } from '../CustomerSelection';
import { EditType } from '../item/EditType';
import { ItemCustomsPanel } from '../panels/ItemCustomsPanel';
import { ItemExtrasPanel } from '../panels/ItemExtrasPanel';
import { PanelButtons } from '../panels/PanelButtons';
import { GenericEditProps } from './Edit';
import { EditMetaModel } from '../ItemMeta';
import { IossNumber } from '../../../lib/consts/ItemMetaConstants';
import { reconcileLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';
import { LocalUnitsEntry } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { ItemExtrasAutoApplier } from '../../../lib/helpers/ItemExtrasAutoApplier';
import { EventSections } from '../../../lib/events/EventLogger';
import { DeliveredDutyPaidExtra, PackageExtras } from '../../ExtrasHelper';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ItemContentsPanel } from '../panels/ItemContentsPanel';
import { SanitiseConsignmentForSave, SplitConsignmentWeight } from '../panels/consignments/GetConsignmentFromItem';
import {
  applyOrderLineSync,
  contentsWereRegenerated,
  OrderLineSyncPlan,
  OrderLineSyncResult
} from '~/apps/smartsend/lib/parcels/syncOrderLines';
import { GenerateConsignmentFromItem } from '../panels/consignments/GenerateConsignmentFromItem';
import { ItemCollectionDateSection } from '../ItemCollectionDateSection';
import GetTariffSystemFromItem from '../../../lib/helpers/GetTariffSystemFromItem';
import { CustomerMeta } from '../CustomerMeta';
import { ProtectionIsEnabled } from '../../../lib/helpers/ProtectionIsEnabled';
import { onlyUnique } from '../../../lib/helpers/ArrayHelper';
import GetDeliveredDutyShippingCost from '../../../lib/helpers/GetDeliveredDutyShippingCost';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { IsWindsor } from '~/apps/smartsend/lib/helpers/IsWindsor';
import { SidePanelSections } from '../../../../../components/sidepanel-section';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import useCountries from '~/hooks/use-countries';
import { ItemTaxIds } from '../panels/ItemTaxIds';
import { stateCustomerIsSender } from '~/apps/smartsend/lib/context/ContextFunctions';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { ToSmartSendAnalyticsModel, ToSmartSendAnalyticsPatchModel } from '../quotes/utils/CurrentSelectionType';

export default function ItemDetails(props: GenericEditProps) {
  const isXs = useP2GMediaQueryOnly('xs');
  const ctx = useContext(SmartSendContext);
  const {
    getCustomers,
    getDeliveredDutyPaidCalculation,
    getPackages,
    getQuoteOptions,
    getQuoteWithValidation,
    updateCustomer
  } = useSmartSendApi();
  const { getLocalisedCountries } = useCountries();
  const [item, setItem] = useState<SmartSendItemApiModel>(
    ItemExtrasAutoApplier(JSON.parse(JSON.stringify({ ...props.item })))
  );
  const [originalItem] = useState<SmartSendItemApiModel>(
    ItemExtrasAutoApplier(JSON.parse(JSON.stringify({ ...props.item })))
  );
  /**
   * The lines as they stood when the consignment parcels last agreed with them, and the merge base
   * planOrderLineSync diffs against.
   *
   * State rather than a read of originalItem because it has to ADVANCE. Change a SKU OLD -> A, apply
   * it to the parcels, then change it A -> B: every row now holds A, so a frozen baseline of OLD
   * matches nothing and the offer to apply would never come back. It advances on apply, and whenever
   * the parcels are rebuilt from the lines (see updateConsignment).
   */
  const [syncBaseline, setSyncBaseline] = useState<MarketplaceOrderApiModel[]>(
    () => originalItem.MarketplaceOrderLines ?? []
  );
  const [tab, setTab] = useState(props.editType);
  const [changes, SetChanges] = useState<string[]>([]);
  const state = props.item.State;
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    track({
      type: `edit_item_loaded`,
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, 'order-details-panel']),
        action: props.editType
      }
    });
  }, [props.editType, track]);

  const onSave = async () => {
    const actions: ApplierOption[] = [];

    if (DimensionsHaveChanged(props.item, item.PackageDimensions)) {
      actions.push('Dimensions');
    }

    if (CustomerHasChanged(props.item.Sender, item.Sender)) {
      actions.push('Sender');
      if (
        SettingsCache.settings.Features.includes('SaveAddressOnSenderUpdate') &&
        stateCustomerIsSender(ctx.state.customer, item.Sender)
      ) {
        await updateCustomer(item.Sender.Id, item.Sender);
      }
    }

    if (CustomerHasChanged(props.item.Recipient, item.Recipient)) {
      actions.push('Recipient');
    }

    if (ExtrasHaveChanged(props.item.Extras, item.Extras)) {
      actions.push('Extras');
    }

    if (CollectionDateHasChanged(props.item, item.PreferredCollectionDate)) {
      actions.push('PreferredCollectionDate');
    }

    if (MetaHasChanged(props.item.Meta, item.Meta)) {
      actions.push('Meta');
    }

    // If we have a consignment, consolidate the parcels
    SanitiseConsignmentForSave(item);
    if (ConsignmentHasChanged(props.item.Consignment, item.Consignment)) {
      actions.push('Consignment');
    }

    if (OrderLinesHaveChanged(props.item.MarketplaceOrderLines, item.MarketplaceOrderLines)) {
      actions.push('MarketplaceOrderLines');
    }

    if (actions.length > 0) {
      const patchModel = ToSmartSendAnalyticsPatchModel(ToSmartSendAnalyticsModel(item), actions);
      track({
        type: 'item_edited',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, 'order-details-panel']),
          surface: 'order-details-panel',
          action: 'save-item-edits',
          changedFields: actions,
          itemScope: 'single',
          originalItems: [ToSmartSendAnalyticsModel(originalItem)],
          patchModel: patchModel
        }
      });
    }

    ctx.dispatch(
      new SaveItemsAction([item.Id], actions, {
        Dimensions: item.PackageDimensions,
        Sender: item.Sender,
        Recipient: item.Recipient,
        Extras: item.Extras,
        Meta: item.Meta,
        MarketplaceOrderLines: item.MarketplaceOrderLines,
        PreferredCollectionDate: item.PreferredCollectionDate,
        Consignment: item.Consignment
      })
    );
    props.onClose();
  };

  const changeTab = (tab: EditType) => {
    setTab(tab);
  };

  const onItemMetaSave = (u: EditMetaModel) => {
    const met = { ...item.Meta };
    met[u.key] = u.meta;
    setItem({ ...item, Meta: met });
  };

  const onExtrasSave = (extras: QuoteExtraApiModel[]) => {
    setItem({ ...item, Extras: extras });
  };

  const onPreferredCollectionDate = (date: Date) => {
    setItem({
      ...item,
      PreferredCollectionDate: date
    });
  };

  const onItemCustomsUpdate = (u: SmartSendItemApiModel) => {
    // If they've taken DDP, but have an IOSS number. Wipe it out.
    if (u.Extras.find((x) => x.Id === DeliveredDutyPaidExtra) && u.Meta[IossNumber]?.Value) {
      u.Meta[IossNumber].Value = '';
    }

    setItem({
      ...item,
      PackageDimensions: u.PackageDimensions,
      Sender: u.Sender,
      Recipient: u.Recipient,
      Extras: u.Extras,
      MarketplaceOrderLines: u.MarketplaceOrderLines,
      Meta: u.Meta
    });
  };

  const getValidation = async () => {
    const r = await getQuoteWithValidation(item);
    return r.success ? r.result : null;
  };

  const getQuote = async () => {
    if (!item.Service) {
      return null;
    }
    const r = await getQuoteOptions(
      item.Service?.Provider,
      item.Service?.AccountId,
      ItemExtrasAutoApplier(item),
      item.Service?.Id
    );
    return r.success ? r.result.Quotes[0] : null;
  };

  const getCustomersList = async () => {
    const x = await getCustomers();
    return x.result.Customers;
  };

  const getPackagesList = async () => {
    const r = await getPackages();
    return r.success ? r.result.Packages : [];
  };

  const getDdpCalc = async () => {
    const option = state?.validation?.QuoteState?.Quote?.Options?.[0];
    const shippingCost = GetDeliveredDutyShippingCost(option);
    try {
      const calculation = await getDeliveredDutyPaidCalculation(
        item.Service.Provider,
        item.Service.AccountId,
        item,
        shippingCost
      );
      return calculation.success ? calculation.result : null;
    } catch {
      return null;
    }
  };

  const updateConsignment = (c: ConsignmentApiModel) => {
    const i = { ...item };
    i.Consignment = c;
    i.PackageDimensions.Weight = GetItemWeight(i);

    // Rebuilt parcels were derived from the CURRENT lines, so the two already agree and the old
    // baseline would be diffing against values no row holds any more. Redistributing carries the
    // existing rows over, which is why this asks whether the contents were regenerated rather than
    // whether the consignment changed.
    if (contentsWereRegenerated(item.Consignment, c)) {
      setSyncBaseline(i.MarketplaceOrderLines ?? []);
    }

    setItem(i);
  };

  /**
   * Carries the line edits the user has just made onto the parcel rows that still hold the pre-edit
   * values. Staged like every other edit here - the user still presses Save.
   */
  const onApplyOrderLineSync = (plan: OrderLineSyncPlan): OrderLineSyncResult => {
    const result = applyOrderLineSync(item.Consignment, plan);

    if (result.applied) {
      const i = { ...item };
      i.Consignment = result.consignment;
      setItem(i);
      // Lines and parcels agree again, so this is the new merge base.
      setSyncBaseline(i.MarketplaceOrderLines ?? []);
    }

    // Returned so the notice can report what actually landed rather than guessing from the plan.
    return result;
  };

  /**
   * Throws the parcels away and rebuilds them from the order lines as they stand now.
   *
   * The escape hatch for when applying cannot help - every matching row was customised, so there is
   * nowhere safe to write. Deliberately GenerateConsignmentFromItem and not
   * RedistributeConsignmentParcels: redistributing carries the existing rows over, preserving exactly
   * the edits a rebuild is meant to discard. Being the same call as first-time generation is the point,
   * so there is no second half-preserving rebuild path to reason about.
   *
   * The parcel COUNT survives, which is what separates this from sending as a single parcel - it is a
   * packing decision rather than anything derived from the marketplace. The item's own dimensions
   * survive too; GenerateConsignmentFromItem just re-splits their weight across the parcels.
   *
   * No baseline handling needed: this mints fresh row UIDs, so updateConsignment's
   * contentsWereRegenerated branch resets it.
   */
  const rebuildConsignmentFromOrderLines = () => {
    const parcelCount = item.Consignment?.Parcels?.length;

    if (!parcelCount) {
      return;
    }

    updateConsignment(GenerateConsignmentFromItem(item, item.PackageDimensions, parcelCount));
  };

  const reset = () => {
    props.reset(tab);
    SetChanges([]);
    setSyncBaseline(originalItem.MarketplaceOrderLines ?? []);
  };

  const onContentUpdate = (value: number, items: MarketplaceOrderApiModel[]) => {
    const MPOrdersToStore = props.item.MarketplaceOrderLines.map((loopItem) => {
      const match = items.find((r) => r.Id === loopItem.Id);
      if (!match) return loopItem;
      return {
        ...loopItem,
        Product: { ...loopItem.Product, ...match.Product },
        Value: match.Value,
        Quantity: match.Quantity
      };
    });
    setItem({ ...item, MarketplaceOrderLines: MPOrdersToStore, Value: value });
  };

  const onDimensionsUpdate = (dimensions: Dimensions, localUnitsEntry?: LocalUnitsEntry) => {
    const i = { ...item };
    i.PackageDimensions = { ...dimensions };
    // This only gets called if they have minimised or are applying to all.
    if (i.Consignment) {
      i.Consignment.Parcels.forEach((p) => {
        p.Dimensions = { ...dimensions };
        // Each parcel now holds the item's dimensions verbatim, so the item's as-entered entry is the
        // truth for them too and any prior per-parcel entry describes values that no longer exist.
        // SplitConsignmentWeight then drops the weight original from any parcel whose share is divided.
        p.LocalUnits = localUnitsEntry ? { ...localUnitsEntry } : undefined;
      });
      SplitConsignmentWeight(dimensions, i.Consignment);
    }
    i.Meta = { ...i.Meta };
    reconcileLocalUnitsEntry(i.Meta, localUnitsEntry);

    setItem(i);
  };

  const tariffSystem = GetTariffSystemFromItem(props.item);
  const requiresCustomsForItem = RequiresCustoms(state?.validation);
  const requiresTaxIdsForItem = !requiresCustomsForItem && RequiresTaxIdsButNotCustoms(state?.validation);
  const canShowExtras = !!item.Service?.Id;

  return (
    <>
      <P2GBox mb={2}>
        {isXs || SettingsCache.settings.Features.includes('DropDownPanelNavigation') ? (
          <P2GDropdown
            id="View Details:"
            onChange={changeTab}
            options={[
              { value: 'ADDRESS', text: t('EDIT_ORDER_ADDRESS_TAB', { defaultValue: 'Address Details' }) },
              { value: 'CONTENTS', text: t('EDIT_ORDER_CONTENTS_TAB', { defaultValue: 'Parcel Contents' }) },
              { value: 'EXTRAS', text: t('EDIT_ORDER_EXTRAS_TAB', { defaultValue: 'Available Extras' }) },
              ...(requiresCustomsForItem
                ? [
                    {
                      value: 'CUSTOMS',
                      text: IsWindsor(item)
                        ? t('EDIT_ORDER_OTHER_TAB', { defaultValue: 'Other' })
                        : t('EDIT_ORDER_CUSTOMS_TAB', { defaultValue: 'Customs Information' })
                    }
                  ]
                : []),
              ...(requiresTaxIdsForItem
                ? [{ value: 'CUSTOMS', text: t('EDIT_ORDER_TAX_IDS_TAB', { defaultValue: 'Tax Ids' }) }]
                : [])
            ]}
            value={tab}
          />
        ) : (
          <P2GButtonGroup fullWidth>
            <P2GButtonGroupItem value="ADDRESS" selected={tab} onClick={changeTab}>
              {t('EDIT_ORDER_ADDRESS_TAB', { defaultValue: 'Address' })} {changes.includes('ADDRESS') && '*'}
            </P2GButtonGroupItem>
            <P2GButtonGroupItem value="CONTENTS" selected={tab} onClick={changeTab}>
              {item.Consignment?.Parcels?.length > 0
                ? t('EDIT_ORDER_PARCELS_TAB', { defaultValue: 'Parcels' })
                : t('EDIT_ORDER_CONTENTS_TAB', { defaultValue: 'Contents' })}{' '}
              {changes.includes('CONTENTS') && '*'}
            </P2GButtonGroupItem>
            {canShowExtras && (
              <P2GButtonGroupItem value="EXTRAS" selected={tab} onClick={changeTab}>
                {t('EDIT_ORDER_EXTRAS_TAB', { defaultValue: 'Extras' })} {changes.includes('EXTRAS') && '*'}
              </P2GButtonGroupItem>
            )}
            {requiresCustomsForItem && (
              <P2GButtonGroupItem value="CUSTOMS" selected={tab} onClick={changeTab}>
                {IsWindsor(item)
                  ? t('EDIT_ORDER_OTHER_TAB', { defaultValue: 'Other' })
                  : t('EDIT_ORDER_CUSTOMS_TAB', { defaultValue: 'Customs' })}
                {changes.includes('CUSTOMS') && ' *'}
              </P2GButtonGroupItem>
            )}
            {requiresTaxIdsForItem && (
              <P2GButtonGroupItem value="CUSTOMS" selected={tab} onClick={changeTab}>
                {t('EDIT_ORDER_TAX_IDS_TAB', { defaultValue: 'Tax Ids' })}
              </P2GButtonGroupItem>
            )}
          </P2GButtonGroup>
        )}
      </P2GBox>
      <P2GBox onClick={() => SetChanges([...changes, tab].filter(onlyUnique))}>
        {tab === 'EXTRAS' && (
          <>
            {canShowExtras ? (
              <ItemExtrasPanel
                protectionEnabled={ProtectionIsEnabled(item)}
                onUpdate={onExtrasSave}
                showNonFoundMessage={true}
                getQuote={getQuote}
                currentSelection={item.Extras}
                currentCoverPercentage={item.State?.validation?.QuoteState?.Quote?.Options[0]?.CoverPercentage || 0}
                item={item}
                extrasBlackList={PackageExtras}
              />
            ) : (
              <P2GAlert>
                {t('EDIT_ORDER_EXTRAS_NO_SERVICE', {
                  defaultValue: 'You must select a service before you can select extras.'
                })}
              </P2GAlert>
            )}
          </>
        )}
        {tab === 'CONTENTS' && (
          <ItemContentsPanel
            onConsignmentUpdate={updateConsignment}
            item={item}
            onDimensionsUpdate={onDimensionsUpdate}
            getCountries={getLocalisedCountries}
            getPackages={getPackagesList}
            getValidation={getValidation}
            onClose={props.onClose}
            tariffSystem={tariffSystem}
            hideDimensionsIfNotRequired={SettingsCache.settings.Features.includes('HideDimensionsIfNotRequired')}
            onContentUpdate={onContentUpdate}
            baselineOrderLines={syncBaseline}
            onApplyOrderLineSync={onApplyOrderLineSync}
            onRebuildParcels={rebuildConsignmentFromOrderLines}
            currentSelection={item.Extras}
            getQuote={getQuote}
            onExtrasUpdate={onExtrasSave}
            extrasWhiteList={PackageExtras}
            // This will do a quote request for extras so limit to feature check as only on for PM USA.
            canShowExtras={canShowExtras && SettingsCache.settings.Features.includes('AdditionalHandling')}
          />
        )}
        {tab === 'CUSTOMS' && (
          <SidePanelSections>
            {requiresCustomsForItem && (
              <ItemCustomsPanel
                item={item}
                onUpdate={onItemCustomsUpdate}
                changeTab={changeTab}
                getValidation={getValidation}
                getQuote={getQuote}
                getDeliveryDutyPaidCalculation={getDdpCalc}
              />
            )}
            {requiresTaxIdsForItem && (
              <ItemTaxIds item={item} onUpdate={onItemCustomsUpdate} getValidation={getValidation} />
            )}
          </SidePanelSections>
        )}

        {tab === 'ADDRESS' && (
          <SidePanelSections>
            {item.State?.validation?.QuoteState?.Quote?.Service?.CollectionType === 'Collection' &&
              SettingsCache.settings.Features.includes('CollectionDates') && (
                <ItemCollectionDateSection
                  variant={'body2'}
                  dates={item.State.validation?.QuoteState?.Dates}
                  selected={item.PreferredCollectionDate}
                  title={t('COLLECTION_DATE_SUBTITLE', { defaultValue: 'Collection Date:' })}
                  onUpdate={onPreferredCollectionDate}
                />
              )}
            <CustomerSelection
              item={item}
              onUpdate={(i) => setItem({ ...item, Recipient: i.Recipient, Sender: i.Sender })}
              changeTab={changeTab}
              getValidation={getValidation}
              getCustomer={getCustomersList}
              getCountries={getLocalisedCountries}
              isReturn={IsReturnCollection(item)}
            />
            <CustomerMeta meta={item.Meta} onUpdate={onItemMetaSave} />
          </SidePanelSections>
        )}
        {changes.length > 0 && (
          <P2GBox mt={2}>
            <P2GAlert
              type="warning"
              icon={
                <CdnIconProxy size="sm" src="/theme/icons/Edit.svg">
                  <SvgEdit />
                </CdnIconProxy>
              }
            >
              <P2GText variant="body2">
                <Trans
                  i18nKey="UNSAVED_CHANGES_WARNING_TEXT"
                  t={t}
                  values={{
                    change: ToCommaList(changes.map((x) => x.toLowerCase()))
                  }}
                  defaults=" You have unsaved changes on your <strong>{{change}}</strong>"
                />
              </P2GText>
              <P2GText variant="body2">
                <Trans
                  i18nKey="UNSAVED_CHANGES_RESET_TEXT"
                  t={t}
                  components={{ reset: <P2GLink onClick={reset} /> }}
                  defaults="You can <reset>reset</reset> your changes."
                />
              </P2GText>
            </P2GAlert>
          </P2GBox>
        )}
        <P2GBox mt={2} />
        <PanelButtons saveDisabled={changes.length === 0} onCancel={props.onClose} onSave={onSave} />
      </P2GBox>
    </>
  );
}
